import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, RotateCcw, Heart, Share2, Edit3, Download } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { FlashCard } from '../../lib/gemini';
import { downloadFlashcards } from '../../utils/flashcardDownload';

interface FlashcardViewerProps {
  flashcards: FlashCard[];
  onComplete?: () => void;
}

export const FlashcardViewer: React.FC<FlashcardViewerProps> = ({ flashcards, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [studyMode, setStudyMode] = useState<'normal' | 'review' | 'favorites'>('normal');

  const currentCard = flashcards[currentIndex];
  const progress = ((currentIndex + 1) / flashcards.length) * 100;

  const nextCard = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else if (onComplete) {
      // Store flashcard session metadata in localStorage
      const flashcardMeta = {
        count: flashcards.length,
        completedAt: new Date().toISOString(),
      };
      const existingFlashcards = JSON.parse(localStorage.getItem('ecstatic_uploaded_flashcards') || '[]');
      existingFlashcards.unshift(flashcardMeta);
      localStorage.setItem('ecstatic_uploaded_flashcards', JSON.stringify(existingFlashcards));
      onComplete();
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const toggleFavorite = () => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(currentIndex)) {
      newFavorites.delete(currentIndex);
    } else {
      newFavorites.add(currentIndex);
    }
    setFavorites(newFavorites);
  };

  const resetCards = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleDownload = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `flashcards-${timestamp}`;
    downloadFlashcards(flashcards, filename);
    // Show success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full';
    notification.textContent = `✅ Downloaded all flashcards as PDF!`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  };

  const handleDownloadFavorites = () => {
    if (favorites.size === 0) {
      alert('No favorite cards selected. Add some favorites first!');
      return;
    }
    const favoriteCards = flashcards.filter((_, index) => favorites.has(index));
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `favorite-flashcards-${timestamp}`;
    downloadFlashcards(favoriteCards, filename);
    
    // Show success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full';
    notification.textContent = `✅ Downloaded ${favoriteCards.length} favorite flashcards as PDF!`;
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextCard,
    onSwipedRight: prevCard,
    onSwipedUp: () => setIsFlipped(!isFlipped),
    trackMouse: true
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowRight':
        case ' ':
          event.preventDefault();
          nextCard();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          prevCard();
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          event.preventDefault();
          setIsFlipped(!isFlipped);
          break;
        case 'f':
          event.preventDefault();
          toggleFavorite();
          break;
        case 'r':
          event.preventDefault();
          resetCards();
          break;
        case 'd':
          event.preventDefault();
          handleDownload();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isFlipped]);

  if (!flashcards.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No flashcards available</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 px-2 sm:px-4 md:px-0 overflow-x-hidden">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-2 h-2.5">
        <motion.div
          className="bg-primary-600 h-2 sm:h-2 h-2.5 rounded-full"
          style={{ width: `${progress}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Card Counter */}
      <div className="text-center">
        <p className="text-sm sm:text-sm text-xs text-gray-500 dark:text-gray-400">
          Card {currentIndex + 1} of {flashcards.length}
        </p>
      </div>

      {/* Flashcard */}
      <div {...swipeHandlers} className="relative h-60 sm:h-72 md:h-96">
        <motion.div
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsFlipped(!isFlipped)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentIndex}-${isFlipped}`}
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: -90, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full h-full"
            >
              <div className="h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2 sm:p-4 md:p-8 flex flex-col justify-center items-center text-center">
                <div className="mb-2 sm:mb-4">
                  {isFlipped ? (
                    <div className="text-xs sm:text-xs text-[11px] text-primary-600 dark:text-primary-400 font-medium mb-1 sm:mb-2">
                      DEFINITION
                    </div>
                  ) : (
                    <div className="text-xs sm:text-xs text-[11px] text-secondary-600 dark:text-secondary-400 font-medium mb-1 sm:mb-2">
                      TERM
                    </div>
                  )}
                </div>
                
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-base sm:text-lg md:text-xl font-medium text-gray-900 dark:text-white leading-relaxed break-words">
                    {isFlipped ? currentCard.definition : currentCard.term}
                  </p>
                </div>
                
                <div className="mt-2 sm:mt-4 text-xs sm:text-xs text-[11px] text-gray-400 dark:text-gray-500">
                  Click to flip • Swipe to navigate
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-0">
        <button
          onClick={prevCard}
          disabled={currentIndex === 0}
          className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Previous</span>
        </button>

        <div className="flex flex-row items-center justify-center space-x-2 my-2 sm:my-0">
          <button
            onClick={toggleFavorite}
            className={`p-2 rounded-lg transition-colors min-w-[44px] min-h-[44px] ${
              favorites.has(currentIndex)
                ? 'bg-error-100 text-error-600 dark:bg-error-900/20 dark:text-error-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            <Heart className="h-4 w-4" />
          </button>
          
          <button
            onClick={resetCards}
            className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors min-w-[44px] min-h-[44px]"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          
          <button
            onClick={handleDownload}
            className="p-2 bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/30 transition-colors min-w-[44px] min-h-[44px]"
            title="Download all flashcards as PDF"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>

        <button
          onClick={nextCard}
          disabled={currentIndex === flashcards.length - 1}
          className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors text-sm"
        >
          <span>{currentIndex === flashcards.length - 1 ? 'Complete' : 'Next'}</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Keyboard shortcuts */}
      <div className="text-center text-xs sm:text-xs text-[11px] text-gray-400 dark:text-gray-500 space-y-1 pb-2">
        <p>Keyboard shortcuts:</p>
        <p>← → Navigation • ↑ ↓ Flip card • F Favorite • R Reset • D Download all flashcards as PDF</p>
      </div>
    </div>
  );
};